import { z } from 'zod';
import { EnergySavings } from './projects.js';
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Reports (AFM requirements c, e, i, j): stored, immutable snapshots with PDF / XLSX / CSV files. */
export const ReportType = z.enum([
    'functioning',
    'energy_annual',
    'light_points',
    'monitoring_annual',
    'installation_sheet',
    'inventory',
]);
export const ReportFormat = z.enum(['pdf', 'xlsx', 'csv']);
export const Report = z.object({
    id: z.uuid(),
    type: ReportType,
    client_id: z.uuid(),
    client_name: z.string(),
    project_id: z.uuid().nullable(),
    project_name: z.string().nullable(),
    site_id: z.uuid().nullable(),
    site_name: z.string().nullable(),
    period_from: Day.nullable(),
    period_to: Day.nullable(),
    title: z.string(),
    locale: z.enum(['en', 'ro']),
    trigger: z.enum(['manual', 'automatic']),
    status: z.enum(['pending', 'ready', 'failed']),
    summary: z.record(z.string(), z.unknown()),
    error: z.string().nullable(),
    generated_by: z.string().nullable(),
    created_at: z.string(),
    finished_at: z.string().nullable(),
    formats: z.array(ReportFormat),
});
export const ReportList = z.object({ items: z.array(Report) });
/** POST /api/v1/reports — "Generează raport". The period defaults per type (see the reports module). */
export const ReportCreate = z.object({
    type: ReportType,
    client_id: z.uuid(),
    project_id: z.uuid().optional(),
    site_id: z.uuid().optional(),
    period_from: Day.optional(),
    period_to: Day.optional(),
    /** energy_annual / monitoring_annual: the calendar year */
    year: z.number().int().min(2000).max(2100).optional(),
    locale: z.enum(['en', 'ro']).optional(),
    /** at the stored-reports quota: confirm that the caller's oldest report is deleted to make room */
    replace_oldest: z.boolean().optional(),
});
/**
 * GET /api/v1/reports/quota — the caller's report quota (accounts without report.unlimited, i.e. the authority
 * observer): reports generated today and reports kept, with the oldest one that a new report would replace.
 */
export const ReportQuota = z.object({
    limited: z.boolean(),
    daily_limit: z.number().int(),
    used_today: z.number().int(),
    total_limit: z.number().int(),
    total: z.number().int(),
    oldest: z.object({ id: z.uuid(), title: z.string(), created_at: z.string() }).nullable(),
});
/** GET /api/v1/reports/overview?client_id&project_id — live indicators of the AFM / client dashboard. */
export const ReportOverview = z.object({
    client_id: z.uuid(),
    client_name: z.string(),
    project: z
        .object({
        id: z.uuid(),
        name: z.string(),
        start_date: Day.nullable(),
        end_date: Day.nullable(),
        status: z.string(),
    })
        .nullable(),
    functioning: z.object({
        total: z.number().int(),
        seen_24h: z.number().int(),
        seen_7d: z.number().int(),
        never: z.number().int(),
        with_gps: z.number().int(),
        online: z.number().int(),
        /** null: the caller may not see alarms (authority observer) */
        active_alarms: z.number().int().nullable(),
        active_critical: z.number().int().nullable(),
        availability_pct: z.number().nullable(),
        hours_avg_night: z.number().nullable(),
        hours_avg_lifetime: z.number().nullable(),
        alarms_opened: z.number().int().nullable(),
        alarms_resolved: z.number().int().nullable(),
        mttr_h: z.number().nullable(),
        by_protocol: z.array(z.object({ protocol: z.string(), n: z.number().int() })),
    }),
    /** this calendar year so far */
    energy_ytd: EnergySavings,
    /** whole project (start → today), when a project is selected */
    energy_project: EnergySavings.nullable(),
    /** availability per day, last 30 days (0–100) */
    availability_daily: z.array(z.object({ day: Day, pct: z.number().nullable() })),
    last_reports: z.array(Report),
});
/** GET /api/v1/reports/light-points — the per light point table (point e) as JSON. */
export const LightPointRow = z.object({
    id: z.uuid(),
    name: z.string(),
    site: z.string().nullable(),
    serial: z.string().nullable(),
    protocol: z.string().nullable(),
    lat: z.number().nullable(),
    lon: z.number().nullable(),
    presence: z.string(),
    measured_at: z.string().nullable(),
    dimmer_pct: z.number().nullable(),
    programmed_pct: z.number().nullable(),
    /** null also when the caller may not see groups (authority observer) */
    group: z.string().nullable(),
    power_w: z.number().nullable(),
    hours_lifetime: z.number().nullable(),
    hours_avg_night: z.number().nullable(),
    energy_lifetime_kwh: z.number().nullable(),
    energy_period_kwh: z.number().nullable(),
    nominal_w: z.number().nullable(),
    nominal_source: z.string(),
    /** null: the caller may not see alarms (authority observer) */
    active_alarms: z.number().int().nullable(),
});
export const LightPointList = z.object({
    items: z.array(LightPointRow),
    from: Day,
    to: Day,
    avg_hours_lifetime: z.number().nullable(),
});
//# sourceMappingURL=reports.js.map